import { auth } from '../firebase';
import { Type } from "@google/genai";
import { callServerAI } from "./geminiClient";
import { MODELS } from "../config/models";
import { fetchFMP, fetchPolygon, getProxyAuthHeaders } from "./dataFetchers";
import { withRetry, cleanJson, extractText } from "./aiUtilities";
import { MarketCapCategory, PreInflectionResult, ScreenerOutput, ScreenerDiagnostics, PricePoint, MarketContext, PreInflectionScreenOutput, PreInflectionScreenResult } from "../types/geminiTypes";
export interface PreInflectionStageCallbacks {
  onFetchComplete?: () => void;
  onAnalysisStart?: () => void;
  onAnalysisComplete?: () => void;
}
export async function detectPreInflection(ticker: string, currentDateTime: string): Promise<PreInflectionResult> {
  const symbol = ticker.toUpperCase();

  // Fetch quarterly data for trend analysis
  let quarterlyData: any = null;
  try {
      const [
        income, 
        balance, 
        cashFlow, 
        profile, 
        news
      ] = await Promise.all([
        fetchFMP(`stable/income-statement`, { symbol, limit: "8", period: "quarter" }),
        fetchFMP(`stable/balance-sheet-statement`, { symbol, limit: "8", period: "quarter" }),
        fetchFMP(`stable/cash-flow-statement`, { symbol, limit: "8", period: "quarter" }),
        fetchFMP(`stable/profile`, { symbol }),
        fetchFMP(`stable/fmp-articles`, { symbol, limit: "10" })
      ]);

    // Prune data to reduce token count
    const pruneStatement = (data: any[]) => {
      if (!Array.isArray(data)) return [];
      return data.map(item => ({
        date: item.date,
        revenue: item.revenue,
        grossProfit: item.grossProfit,
        operatingIncome: item.operatingIncome,
        netIncome: item.netIncome,
        eps: item.eps,
        totalAssets: item.totalAssets,
        totalLiabilities: item.totalLiabilities,
        totalStockholdersEquity: item.totalStockholdersEquity,
        operatingCashFlow: item.operatingCashFlow,
        capitalExpenditure: item.capitalExpenditure,
        freeCashFlow: item.freeCashFlow,
        deferredRevenue: item.deferredRevenue,
        deferredRevenueNonCurrent: item.deferredRevenueNonCurrent
      }));
    };

    quarterlyData = {
      income: pruneStatement(income),
      balance: pruneStatement(balance),
      cashFlow: pruneStatement(cashFlow),
      profile: Array.isArray(profile) ? profile[0] : profile,
      news: Array.isArray(news) ? news.map((n: any) => ({ title: n.title, text: (n.content ?? n.text ?? '').substring(0, 500), site: n.site, date: n.date ?? n.publishedDate ?? '' })) : []
    };

    if (!quarterlyData.profile) {
      throw new Error(`NOT_FOUND: The stock symbol '${symbol}' is not listed on any of the markets that Oracle Insights analyzes, or could not be found.`);
    }
  } catch (err) {
    if (err instanceof Error && err.message.includes("NOT_FOUND")) {
      throw err;
    }
    console.error("Failed to fetch quarterly data for pre-inflection analysis:", err);
  }

  return await withRetry(async () => {
    const prompt = `Perform a "Pre-Inflection Detector" analysis for ${symbol} as of ${currentDateTime}.
    
    The goal is to identify "Emerging Compounders" before they are widely recognized by the market, and to classify their current lifecycle stage.
    
    CRITICAL RULES:
    1. HARD GATING: Narrative Tier CANNOT exceed Monetization Tier. 
       - IF (New Revenue Contribution < 10%) THEN Narrative Tier = MIN(Narrative Tier, Tier 2).
       - Narrative cannot outrun monetization.
    2. REVENUE PERSISTENCE & PENALTY: 
       - Acceleration: Requires ≥2 consecutive quarters of acceleration AND Total acceleration ≥ 300–500 bps.
       - Deceleration Penalty: IF revenue growth decelerates for 2+ consecutive quarters AND margins flatten or decline -> Apply IOS penalty (-10 to -20) -> Bias toward Stage 4.
    3. VOLATILITY FILTER:
       - IF signal direction (revenue/margins) is inconsistent (up/down/up/down) across 8 quarters -> Reduce confidence weight by 25–40%.
    4. DRIVER OF RETURNS & REFINED VALUATION:
       - IF (Price Increase ≈ Earnings Growth) -> Earnings-Driven Phase (Mid-Stage).
       - IF (Price Increase >> Earnings Growth) -> Multiple Expansion Phase (Early).
       - IF (Multiple High AND Growth Normalizing) -> Late-Stage.
       - Market Recognition Gap: Compare valuation to sector median and historical range. IF multiple > sector + 1.5σ -> Strong Late-Stage signal.
    5. TWO-DIMENSIONAL SCORING:
       - Business Quality Score (BQS): Based on ROE, margins, demand durability, and capital efficiency.
       - Inflection Opportunity Score (IOS): Based on acceleration magnitude, recognition gap, and monetization stage.
    6. SIGNAL CONFIDENCE WEIGHTING:
       - Backlog / RPO: High Confidence.
       - Margin Expansion: High Confidence.
       - Revenue Acceleration: Medium Confidence.
       - Narrative Shift: Low Confidence.
       - Final confidenceScore MUST be weighted by these reliability tiers.
    7. STAGE CLASSIFICATION & ACTION:
       - Stage 1: Pre-Inflection (Acceleration just starting, low recognition) -> Action: Small starter.
       - Stage 2: Early Inflection (Signals strong, market not fully aware) -> Action: Aggressive accumulation.
       - Stage 3: Mid-Stage Compounder (Earnings driving returns, still compounding) -> Action: Hold / add on pullbacks.
       - Stage 4: Late-Stage / Fully Priced (Narrative saturated, multiple high) -> Action: Hold / trim.
    8. RE-INFLECTION DETECTION:
       - IF previously Stage 4 AND new acceleration detected (revenue + margins) AND narrative evolves -> Flag as "Re-Inflection Candidate" (isReInflection: true).

    CRITERIA TO EVALUATE:
    1. Backlog & Demand Confirmation: Is forward demand building faster than current revenue? 
       - Look for: Backlog, orders, book-to-bill ratios.
       - Sector Proxies: Deferred revenue growth, RPO (Remaining Performance Obligations), Subscriber adds (for SaaS).
    2. Margin Expansion Trend: Are margins improving for 2+ consecutive quarters?
    3. Capital Efficiency: Is ROE or ROIC trending upward (even if not yet elite)?
    4. Revenue Acceleration (Magnitude & Persistence Filter): 
       - Threshold: ≥2 consecutive quarters of acceleration AND Total acceleration ≥ 300–500 bps.
    5. Proof of Monetization: Is there evidence that new segments or optionality are actually generating revenue? 
       - Threshold: New segment contributes ≥10% of revenue OR shows measurable revenue growth over 2–3 quarters.
    6. Market Recognition Gap: Is the inflection already priced in?
       - Compare current valuation vs historical range AND sector median.
       - Has multiple expansion already occurred? (Early = Low multiple + improving fundamentals; Late = High multiple + confirmed fundamentals).
    7. Narrative Shift (Evidence Scoring):
       - Tier 1: Narrative + NO revenue -> Ignore.
       - Tier 2: Narrative + early monetization -> Emerging.
       - Tier 3: Narrative + scaling revenue -> Confirmed.

    DATA PROVIDED:
    ${JSON.stringify(quarterlyData)}

    INSTRUCTIONS:
    - Analyze the quarterly trends for the last 8 quarters.
    - Look for "future growth leaking into the present".
    - Be speculative but grounded in the provided data.
    - All quantitative inputs (revenue, margins, EPS, cash flow, equity, deferred revenue, and valuation multiples) are provided in the DATA block above and are authoritative. Do NOT use Google Search to find, verify, or override any of these numbers.
    - Google Search is permitted ONLY for qualitative forward-demand context not present in the DATA block — specifically management commentary on backlog, RPO, or book-to-bill direction (e.g. 'growing', 'record', 'softening'). Write ONLY to the top-level "backlogWebEvidence" output field. Never write any search-sourced content — text, descriptions, or numbers — into any signals[] field. All signals[].metrics and signals[].description must derive solely from the provided DATA block. If no relevant commentary is found via search, set "backlogWebEvidence" to null.
    - HARD GATING RULE: Narrative Tier CANNOT exceed Monetization Tier. IF (New Revenue Contribution < 10%) THEN Narrative Tier = MIN(Narrative Tier, 2).
    - PENALTY FOR DECELERATION: IF revenue growth decelerates for 2+ consecutive quarters AND margins flatten/decline, apply a significant penalty to IOS (-10 to -20) and bias toward Stage 4.
    - VOLATILITY FILTER: IF signal direction is inconsistent across 8 quarters, reduce confidence weight by 25-40%.
    - MARKET RECOGNITION GAP: Compare valuation to sector median and historical company range. IF multiple > sector median AND multiple > historical average, bias toward "Fully Priced".

    Provide the results in a structured JSON format.
    {
      "ticker": "${symbol}",
      "name": "Company Name",
      "isPreInflection": boolean,
      "isReInflection": boolean,
      "confidenceScore": number (0-100),
      "businessQualityScore": number (0-100),
      "inflectionOpportunityScore": number (0-100),
      "stage": 1 | 2 | 3 | 4,
      "driverOfReturns": "Multiple Expansion" | "Earnings Driven" | "Fully Priced",
      "suggestedAction": "Small starter" | "Aggressive accumulation" | "Hold / add on pullbacks" | "Hold / trim",
      "volatilityScore": number (0-100),
      "newRevenueContribution": number (0-100),
      "signals": {
        "backlogAcceleration": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of backlog/orders",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "marginExpansion": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of margin trends",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "capitalEfficiency": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of ROE/ROIC trends",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "revenueAcceleration": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of revenue growth trends",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "proofOfMonetization": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of new segment/optionality monetization",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "marketRecognitionGap": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of valuation vs historical range, sector median, and multiple expansion",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        }
      },
      "narrativeEvidenceTier": 1 | 2 | 3,
      "narrativeShift": "Description of any narrative shift",
      "verdict": "Final verdict on whether this is an emerging compounder",
      "historicalContext": "Reconstruct what the company looked like 12-18 months ago vs now",
      "keyTakeaway": "One sentence summary of the edge",
      "backlogWebEvidence": "[SEARCH-ONLY FIELD] Short qualitative phrase from web search describing management commentary on backlog, RPO, or book-to-bill direction. Must be descriptive text only — never a number. Set to null if no relevant commentary was found."
    }
    ESCAPE ALL INNER DOUBLE QUOTES (\\") to avoid JSON parsing errors. DO NOT use markdown code blocks.`;

    const response = await callServerAI({
      model: MODELS.FUNNEL_EDGE.primary,
      contents: prompt,
      config: {
        tools: [{ googleSearch: {} }],
        maxOutputTokens: 8192,
        thinking_level: MODELS.FUNNEL_EDGE.thinkingLevel
      }
    });

    const cleaned = cleanJson(extractText(response));
    const parsed = JSON.parse(cleaned);
    return {
      ...parsed,
      currentPrice: quarterlyData?.profile?.price ?? null,
      priceChangePercent: quarterlyData?.profile?.changePercentage ?? null,
    };
  });
}
export async function detectPreInflectionSSE(
  ticker: string,
  currentDateTime: string,
  callbacks: PreInflectionStageCallbacks
): Promise<PreInflectionResult> {
  const symbol = ticker.toUpperCase();

  // Step 1 — Fetch FMP quarterly data
  let quarterlyData: any = null;
  try {
    const [income, balance, cashFlow, profile, news] = await Promise.all([
      fetchFMP(`stable/income-statement`, { symbol, limit: "8", period: "quarter" }),
      fetchFMP(`stable/balance-sheet-statement`, { symbol, limit: "8", period: "quarter" }),
      fetchFMP(`stable/cash-flow-statement`, { symbol, limit: "8", period: "quarter" }),
      fetchFMP(`stable/profile`, { symbol }),
      fetchFMP(`stable/fmp-articles`, { symbol, limit: "10" })
    ]);

    const pruneStatement = (data: any[]) => {
      if (!Array.isArray(data)) return [];
      return data.map(item => ({
        date: item.date,
        revenue: item.revenue,
        grossProfit: item.grossProfit,
        operatingIncome: item.operatingIncome,
        netIncome: item.netIncome,
        eps: item.eps,
        totalAssets: item.totalAssets,
        totalLiabilities: item.totalLiabilities,
        totalStockholdersEquity: item.totalStockholdersEquity,
        operatingCashFlow: item.operatingCashFlow,
        capitalExpenditure: item.capitalExpenditure,
        freeCashFlow: item.freeCashFlow,
        deferredRevenue: item.deferredRevenue,
        deferredRevenueNonCurrent: item.deferredRevenueNonCurrent
      }));
    };

    quarterlyData = {
      income: pruneStatement(income),
      balance: pruneStatement(balance),
      cashFlow: pruneStatement(cashFlow),
      profile: Array.isArray(profile) ? profile[0] : profile,
      news: Array.isArray(news) ? news.map((n: any) => ({ title: n.title, text: (n.content ?? n.text ?? '').substring(0, 500), site: n.site, date: n.date ?? n.publishedDate ?? '' })) : []
    };

    if (!quarterlyData.profile) {
      throw new Error(`NOT_FOUND: The stock symbol '${symbol}' is not listed on any of the markets that Oracle Insights analyzes, or could not be found.`);
    }
  } catch (err) {
    if (err instanceof Error && err.message.includes("NOT_FOUND")) throw err;
    console.error("Failed to fetch quarterly data for pre-inflection SSE analysis:", err);
  }

  // Signal that FMP data fetch is complete
  callbacks.onFetchComplete?.();

  // Step 2 — Run Gemini analysis
  callbacks.onAnalysisStart?.();

  const result = await withRetry(async () => {
    // Copy the prompt string verbatim from the existing detectPreInflection function.
    // The prompt variable is identical — do not change it.
    const prompt = `Perform a "Pre-Inflection Detector" analysis for ${symbol} as of ${currentDateTime}.
    
    The goal is to identify "Emerging Compounders" before they are widely recognized by the market, and to classify their current lifecycle stage.
    
    CRITICAL RULES:
    1. HARD GATING: Narrative Tier CANNOT exceed Monetization Tier. 
       - IF (New Revenue Contribution < 10%) THEN Narrative Tier = MIN(Narrative Tier, Tier 2).
       - Narrative cannot outrun monetization.
    2. REVENUE PERSISTENCE & PENALTY: 
       - Acceleration: Requires ≥2 consecutive quarters of acceleration AND Total acceleration ≥ 300–500 bps.
       - Deceleration Penalty: IF revenue growth decelerates for 2+ consecutive quarters AND margins flatten or decline -> Apply IOS penalty (-10 to -20) -> Bias toward Stage 4.
    3. VOLATILITY FILTER:
       - IF signal direction (revenue/margins) is inconsistent (up/down/up/down) across 8 quarters -> Reduce confidence weight by 25–40%.
    4. DRIVER OF RETURNS & REFINED VALUATION:
       - IF (Price Increase ≈ Earnings Growth) -> Earnings-Driven Phase (Mid-Stage).
       - IF (Price Increase >> Earnings Growth) -> Multiple Expansion Phase (Early).
       - IF (Multiple High AND Growth Normalizing) -> Late-Stage.
       - Market Recognition Gap: Compare valuation to sector median and historical range. IF multiple > sector + 1.5σ -> Strong Late-Stage signal.
    5. TWO-DIMENSIONAL SCORING:
       - Business Quality Score (BQS): Based on ROE, margins, demand durability, and capital efficiency.
       - Inflection Opportunity Score (IOS): Based on acceleration magnitude, recognition gap, and monetization stage.
    6. SIGNAL CONFIDENCE WEIGHTING:
       - Backlog / RPO: High Confidence.
       - Margin Expansion: High Confidence.
       - Revenue Acceleration: Medium Confidence.
       - Narrative Shift: Low Confidence.
       - Final confidenceScore MUST be weighted by these reliability tiers.
    7. STAGE CLASSIFICATION & ACTION:
       - Stage 1: Pre-Inflection (Acceleration just starting, low recognition) -> Action: Small starter.
       - Stage 2: Early Inflection (Signals strong, market not fully aware) -> Action: Aggressive accumulation.
       - Stage 3: Mid-Stage Compounder (Earnings driving returns, still compounding) -> Action: Hold / add on pullbacks.
       - Stage 4: Late-Stage / Fully Priced (Narrative saturated, multiple high) -> Action: Hold / trim.
    8. RE-INFLECTION DETECTION:
       - IF previously Stage 4 AND new acceleration detected (revenue + margins) AND narrative evolves -> Flag as "Re-Inflection Candidate" (isReInflection: true).

    CRITERIA TO EVALUATE:
    1. Backlog & Demand Confirmation: Is forward demand building faster than current revenue? 
       - Look for: Backlog, orders, book-to-bill ratios.
       - Sector Proxies: Deferred revenue growth, RPO (Remaining Performance Obligations), Subscriber adds (for SaaS).
    2. Margin Expansion Trend: Are margins improving for 2+ consecutive quarters?
    3. Capital Efficiency: Is ROE or ROIC trending upward (even if not yet elite)?
    4. Revenue Acceleration (Magnitude & Persistence Filter): 
       - Threshold: ≥2 consecutive quarters of acceleration AND Total acceleration ≥ 300–500 bps.
    5. Proof of Monetization: Is there evidence that new segments or optionality are actually generating revenue? 
       - Threshold: New segment contributes ≥10% of revenue OR shows measurable revenue growth over 2–3 quarters.
    6. Market Recognition Gap: Is the inflection already priced in?
       - Compare current valuation vs historical range AND sector median.
       - Has multiple expansion already occurred? (Early = Low multiple + improving fundamentals; Late = High multiple + confirmed fundamentals).
    7. Narrative Shift (Evidence Scoring):
       - Tier 1: Narrative + NO revenue -> Ignore.
       - Tier 2: Narrative + early monetization -> Emerging.
       - Tier 3: Narrative + scaling revenue -> Confirmed.

    DATA PROVIDED:
    ${JSON.stringify(quarterlyData)}

    INSTRUCTIONS:
    - Analyze the quarterly trends for the last 8 quarters.
    - Look for "future growth leaking into the present".
    - Be speculative but grounded in the provided data.
    - All quantitative inputs (revenue, margins, EPS, cash flow, equity, deferred revenue, and valuation multiples) are provided in the DATA block above and are authoritative. Do NOT use Google Search to find, verify, or override any of these numbers.
    - Google Search is permitted ONLY for qualitative forward-demand context not present in the DATA block — specifically management commentary on backlog, RPO, or book-to-bill direction (e.g. 'growing', 'record', 'softening'). Write ONLY to the top-level "backlogWebEvidence" output field. Never write any search-sourced content — text, descriptions, or numbers — into any signals[] field. All signals[].metrics and signals[].description must derive solely from the provided DATA block. If no relevant commentary is found via search, set "backlogWebEvidence" to null.
    - HARD GATING RULE: Narrative Tier CANNOT exceed Monetization Tier. IF (New Revenue Contribution < 10%) THEN Narrative Tier = MIN(Narrative Tier, 2).
    - PENALTY FOR DECELERATION: IF revenue growth decelerates for 2+ consecutive quarters AND margins flatten/decline, apply a significant penalty to IOS (-10 to -20) and bias toward Stage 4.
    - VOLATILITY FILTER: IF signal direction is inconsistent across 8 quarters, reduce confidence weight by 25-40%.
    - MARKET RECOGNITION GAP: Compare valuation to sector median and historical company range. IF multiple > sector median AND multiple > historical average, bias toward "Fully Priced".

    Provide the results in a structured JSON format.
    {
      "ticker": "${symbol}",
      "name": "Company Name",
      "isPreInflection": boolean,
      "isReInflection": boolean,
      "confidenceScore": number (0-100),
      "businessQualityScore": number (0-100),
      "inflectionOpportunityScore": number (0-100),
      "stage": 1 | 2 | 3 | 4,
      "driverOfReturns": "Multiple Expansion" | "Earnings Driven" | "Fully Priced",
      "suggestedAction": "Small starter" | "Aggressive accumulation" | "Hold / add on pullbacks" | "Hold / trim",
      "volatilityScore": number (0-100),
      "newRevenueContribution": number (0-100),
      "signals": {
        "backlogAcceleration": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of backlog/orders",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "marginExpansion": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of margin trends",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "capitalEfficiency": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of ROE/ROIC trends",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "revenueAcceleration": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of revenue growth trends",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "proofOfMonetization": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of new segment/optionality monetization",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        },
        "marketRecognitionGap": {
          "status": "positive" | "neutral" | "negative",
          "description": "Analysis of valuation vs historical range, sector median, and multiple expansion",
          "metrics": "Relevant numbers",
          "confidence": "High" | "Medium" | "Low"
        }
      },
      "narrativeEvidenceTier": 1 | 2 | 3,
      "narrativeShift": "Description of any narrative shift",
      "verdict": "Final verdict on whether this is an emerging compounder",
      "historicalContext": "Reconstruct what the company looked like 12-18 months ago vs now",
      "keyTakeaway": "One sentence summary of the edge",
      "backlogWebEvidence": "[SEARCH-ONLY FIELD] Short qualitative phrase from web search describing management commentary on backlog, RPO, or book-to-bill direction. Must be descriptive text only — never a number. Set to null if no relevant commentary was found."
    }
    ESCAPE ALL INNER DOUBLE QUOTES (\\") to avoid JSON parsing errors. DO NOT use markdown code blocks.`;

    const response = await callServerAI({
      model: MODELS.FUNNEL_EDGE.primary,
      contents: prompt,
      config: {
        tools: [{ googleSearch: {} }],
        maxOutputTokens: 8192,
        thinking_level: MODELS.FUNNEL_EDGE.thinkingLevel
      }
    });

    const cleaned = cleanJson(extractText(response));
    const parsed = JSON.parse(cleaned);
    return {
      ...parsed,
      currentPrice: quarterlyData?.profile?.price ?? null,
      priceChangePercent: quarterlyData?.profile?.changePercentage ?? null,
    };
  });

  callbacks.onAnalysisComplete?.();
  return result;
}
export async function getMarketContext(): Promise<MarketContext> {
  const CACHE_KEY = 'oracle_market_context_deterministic';
  const CACHE_DURATION = 1000 * 60 * 60 * 6; // 6 hours

  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    const { data, timestamp } = JSON.parse(cached);
    // Invalidate cache if vix is missing so that new UI can render it immediately
    if (Date.now() - timestamp < CACHE_DURATION && data.vix) {
      return data;
    }
  }

  try {
    let buffettUrl = "/api/buffett-indicator";
    if (typeof window === 'undefined') {
      const port = 3000;
      buffettUrl = `http://127.0.0.1:${port}${buffettUrl}`;
    }
    const response = await fetch(buffettUrl, { headers: await getProxyAuthHeaders() });
    if (!response.ok) {
        throw new Error(`Failed to fetch deterministic buffett indicator: ${response.status}`);
    }
    const data = await response.json();
    
    // Fetch VIX
    let vixData = {
      value: null as number | null,
      sentiment: "Data Unavailable",
      description: "Volatility data is temporarily unavailable. " +
        "Please refresh to try again."
    };
    try {
      let vixUrl = '/api/vix';
      if (typeof window === 'undefined') {
        const port = 3000;
        vixUrl = `http://127.0.0.1:${port}${vixUrl}`;
      }
      const vixRes = await fetch(vixUrl, { headers: await getProxyAuthHeaders() });
      if (vixRes.ok) {
        const vixJson = await vixRes.json();
        if (vixJson.vix) vixData = vixJson.vix;
      }
    } catch (err) {
      console.warn("VIX fetch failed in getMarketContext", err);
    }
    
    const contextData = {
      ...data,
      vix: vixData
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: contextData, timestamp: Date.now() }));
    return contextData;
  } catch (error) {
    console.error("Failed to fetch market context deterministically:", error);
    // Fallback to a safe default if everything fails
    return {
      buffettIndicator: {
        value: null,
        status: "Data Unavailable",
        description: "Market valuation data is temporarily unavailable. Please refresh to try again."
      },
      vix: {
        value: null,
        sentiment: "Data Unavailable",
        description: "Volatility data is temporarily unavailable. Please refresh to try again."
      }
    };
  }
}

function computeQualityComponents(metrics: {
  roic: number;
  fcfYield: number;
  revenueGrowth5Yr: number;
  grossMargin: number;
  netDebtToEbitda: number;
  interestCoverage: number;
  ownerEarningsTrend: string;
}): { profitability: number; consistency: number; efficiency: number; balanceSheet: number; valuation: number } {

  // Profitability (weight 25) — ROIC
  // All metric values are already in percentage form (e.g. roic=18 means 18%).
  let profitability: number;
  if (metrics.roic >= 20) profitability = 100;
  else if (metrics.roic >= 15) profitability = 75;
  else if (metrics.roic >= 10) profitability = 50;
  else if (metrics.roic >= 5) profitability = 25;
  else profitability = 0;

  // Consistency (weight 20) — ownerEarningsTrend + 5yr revenue growth
  let consistency: number;
  if (metrics.ownerEarningsTrend === 'Rising' && metrics.revenueGrowth5Yr >= 8) consistency = 100;
  else if (metrics.ownerEarningsTrend === 'Rising' && metrics.revenueGrowth5Yr >= 4) consistency = 75;
  else if (metrics.ownerEarningsTrend === 'Flat' || metrics.revenueGrowth5Yr >= 0) consistency = 50;
  else consistency = 0;

  // Efficiency (weight 20) — gross margin
  let efficiency: number;
  if (metrics.grossMargin >= 60) efficiency = 100;
  else if (metrics.grossMargin >= 40) efficiency = 75;
  else if (metrics.grossMargin >= 25) efficiency = 50;
  else efficiency = 25;

  // Balance Sheet (weight 20) — net debt / EBITDA + interest coverage
  let balanceSheet: number;
  if (metrics.netDebtToEbitda <= 1.0 && metrics.interestCoverage >= 10) balanceSheet = 100;
  else if (metrics.netDebtToEbitda <= 2.0 && metrics.interestCoverage >= 5) balanceSheet = 75;
  else if (metrics.netDebtToEbitda <= 3.0 && metrics.interestCoverage >= 3) balanceSheet = 50;
  else balanceSheet = 25;

  // Valuation (weight 15) — FCF yield (already in percent)
  let valuation: number;
  if (metrics.fcfYield >= 6) valuation = 100;
  else if (metrics.fcfYield >= 4) valuation = 75;
  else if (metrics.fcfYield >= 3) valuation = 50;
  else valuation = 25;

  return { profitability, consistency, efficiency, balanceSheet, valuation };
}

export async function screenStocks(category: MarketCapCategory = 'large'): Promise<ScreenerOutput> {
  const EXCLUDED_SECTORS = ['Energy', 'Basic Materials'];

  // --- Funnel calibration dials (single values; applied identically to large/mid/small) ---
  // ROIC_FLOOR: a *floor*, not the quality bar. Only removes genuinely capital-destructive
  //   businesses; everything above is scored, not gated.
  const ROIC_FLOOR = 0.08;
  // ROUTING_FCF_YIELD: PRICE line (never a quality gate). At/above -> results (sensibly
  //   priced); below -> nearMisses (wonderful business, wait for a better price).
  const ROUTING_FCF_YIELD = 0.04;

  // Stage 1: Sector-rotating universe fetch.
  //
  // WHY: A static market-cap-descending call always returns the same mega-caps
  // (AAPL, MSFT, NVDA, etc.) which structurally never pass the FCF yield routing
  // gate (≥4%) at current prices. This wastes the most expensive part of the run
  // (5 FMP statement calls per candidate) on companies whose answer is already known.
  //
  // HOW: Three sector groups rotate on a weekly cycle (week number mod 3 — no
  // server state required). Within each week's group, one FMP call is made per
  // sector (parallel), results are combined, deduplicated, Fisher-Yates shuffled,
  // and capped at 100 candidates. This ensures:
  //   - Full sector coverage across three run cycles (~3 weeks)
  //   - Variety within any given week (shuffle draws a different 100 each run)
  //   - Bias toward sectors where sensible FCF yields are more common
  //     (Consumer Defensive, Industrials, Healthcare, Financial Services)
  //   - No meaningful increase in FMP API cost: 2–4 screener calls + 100 × 5
  //     statement calls = ~504 calls vs ~401 today
  //
  // Sector names are exact FMP API strings (verified against stable/company-screener docs).
  // Energy and Basic Materials remain excluded via EXCLUDED_SECTORS as a safety net.

  let candidates: Array<{ symbol: string; name: string; sector: string; industry: string; price: number; marketCap: number }> = [];

  // Sector groups per cap category.
  // Large/mid: 2–4 sectors per group (large universe per sector, 80-limit calls produce plenty).
  // Small: 3 sectors per group (smaller universe per sector — wider windows ensure 100 candidates).
  const SECTOR_ROTATION: Record<MarketCapCategory, string[][]> = {
    large: [
      ['Technology', 'Healthcare'],
      ['Consumer Defensive', 'Consumer Cyclical', 'Industrials'],
      ['Financial Services', 'Communication Services', 'Real Estate', 'Utilities'],
    ],
    mid: [
      ['Technology', 'Healthcare'],
      ['Consumer Defensive', 'Consumer Cyclical', 'Industrials'],
      ['Financial Services', 'Communication Services', 'Real Estate', 'Utilities'],
    ],
    small: [
      ['Technology', 'Healthcare', 'Consumer Defensive'],
      ['Consumer Cyclical', 'Industrials', 'Financial Services'],
      ['Communication Services', 'Real Estate', 'Utilities'],
    ],
  };

  // Fisher-Yates shuffle — different 100 candidates every run within the same sector window.
  const shuffleArray = <T>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // Week-based rotation — changes every Monday at midnight UTC, no server state required.
  const weekGroup = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000)) % 3;
  const activeSectors = SECTOR_ROTATION[category][weekGroup];

  console.log(`[Screener] Stage 1: week-group ${weekGroup} — sectors: [${activeSectors.join(', ')}]`);

  // Captures raw candidate count from inside the Stage 1 try block for use in diagnostics.
  let stage1Raw = 0;

  try {
    // Base params shared across all sector calls.
    // Small cap uses a higher per-sector limit to compensate for the smaller
    // universe per sector relative to large/mid.
    const perSectorLimit = category === 'small' ? '100' : '80';

    const baseParams: Record<string, string> = {
      isActivelyTrading: 'true',
      isEtf: 'false',
      isFund: 'false',
      exchange: 'NYSE,NASDAQ',
      limit: perSectorLimit,
    };

    if (category === 'large') {
      baseParams.marketCapMoreThan = '10000000000';
    } else if (category === 'mid') {
      baseParams.marketCapMoreThan = '2000000000';
      baseParams.marketCapLowerThan = '10000000000';
    } else if (category === 'small') {
      baseParams.marketCapMoreThan = '300000000';
      baseParams.marketCapLowerThan = '2000000000';
    }

    // One FMP screener call per active sector — run in parallel (cheap screener calls).
    const sectorResults = await Promise.all(
      activeSectors.map(sector =>
        fetchFMP('stable/company-screener', { ...baseParams, sector })
          .catch(e => {
            console.warn(`[Screener] Sector fetch failed for ${sector}:`, e);
            return [];
          })
      )
    );

    // Combine → deduplicate by symbol → shuffle → apply exclusion safety net → cap at 100.
    const combined = sectorResults.flat();
    const seen = new Set<string>();
    const deduped = combined.filter(s => {
      if (!s?.symbol || seen.has(s.symbol)) return false;
      seen.add(s.symbol);
      return true;
    });

    candidates = shuffleArray(deduped)
      .filter(s => !EXCLUDED_SECTORS.includes(s.sector))
      .map(s => ({
        symbol: s.symbol,
        name: s.companyName || s.symbol,
        sector: s.sector || 'Unknown',
        industry: s.industry || 'Unknown',
        price: s.price || 0,
        marketCap: s.marketCap || 0,
      }))
      .slice(0, 100);

    stage1Raw = deduped.length;
    console.log(`[Screener] Stage 1: ${deduped.length} raw candidates → ${candidates.length} after shuffle/exclusions/cap.`);
  } catch (e) {
    console.error('[Screener] Stage 1 failed:', e);
    return { results: [], nearMisses: [] };
  }

  // Stage 2: compute metrics
  const BATCH_SIZE = 5;
  
  interface QualifiedCandidate {
    ticker: string;
    name: string;
    sector: string;
    industry: string;
    currentPrice: number;
    pricedIn: boolean;
    metrics: {
      roic: number;
      roe: number;
      debtToEquity: number;
      interestCoverage: number;
      peRatio: number;
      fcfYield: number;
      revenueGrowth5Yr: number;
      grossMargin: number;
      netDebtToEbitda: number;
      ownerEarningsTrend: string;
    };
    fcfConsistencyFlag: boolean;
    ownerEarningsApprox: boolean;
  }
  
  let survivors: QualifiedCandidate[] = [];

  // --- TEMP DIAGNOSTIC: durability-gate rejection counters (remove after validation) ---
  const gateRejections = {
    insufficientData: 0,
    netIncomeNeg: 0,
    fcfNeg: 0,
    fcfSwings: 0,
    interestCoverage: 0,
    roicFloor: 0,
    oeNotRising: 0,
  };

  for (let i = 0; i < candidates.length; i += BATCH_SIZE) {
    const batch = candidates.slice(i, i + BATCH_SIZE);
    
    const batchPromises = batch.map(async (c) => {
      try {
        const [incomeData, cashData, bsData, keyMetricsData, ownerEarningsDataRaw] = await Promise.all([
          fetchFMP(`stable/income-statement`, { symbol: c.symbol, limit: "6", period: "annual" }),
          fetchFMP(`stable/cash-flow-statement`, { symbol: c.symbol, limit: "6", period: "annual" }),
          fetchFMP(`stable/balance-sheet-statement`, { symbol: c.symbol, limit: "1", period: "annual" }),
          fetchFMP(`stable/key-metrics`, { symbol: c.symbol, limit: "4", period: "annual" }),
          fetchFMP(`stable/owner-earnings`, { symbol: c.symbol, limit: "5", period: "annual" }).catch(() => null)
        ]);

        if (!Array.isArray(incomeData) || incomeData.length < 5) { gateRejections.insufficientData++; return null; }
        if (!Array.isArray(cashData) || cashData.length < 5) { gateRejections.insufficientData++; return null; }
        if (!Array.isArray(bsData) || bsData.length < 1) { gateRejections.insufficientData++; return null; }
        if (!Array.isArray(keyMetricsData) || keyMetricsData.length < 1) { gateRejections.insufficientData++; return null; }

        const m = keyMetricsData[0];
        const bs0 = bsData[0];
        const inc0 = incomeData[0];

        const equity = bs0.totalStockholdersEquity || 0;
        const totalDebt0 = bs0.totalDebt || 0;
        const cash0 = bs0.cashAndCashEquivalents || 0;

        // --- METRICS (deterministic; from already-fetched statements) ---
        const computedRoe = equity > 0 ? (inc0.netIncome || 0) / equity : 0;
        const roe = (m.roe ?? m.returnOnEquity ?? 0) || computedRoe;

        const investedCapital = totalDebt0 + equity - cash0;
        const nopat = (inc0.operatingIncome || 0) * 0.79;
        const computedRoic = investedCapital > 0 ? nopat / investedCapital : 0;
        const roic = (m.roic ?? m.returnOnInvestedCapital ?? 0) || computedRoic;

        const computedDe = equity > 0 ? totalDebt0 / equity : 0;
        const debtToEquity = (m.debtToEquity ?? m.debtToEquityTTM ?? 0) || computedDe;

        // P/E FIX: stable/key-metrics has no peRatio/priceToEarningsRatio, but it has
        // earningsYield. Derive P/E = 1 / earningsYield.
        const earningsYield = m.earningsYield ?? 0;
        const peRatio = (m.peRatio ?? m.priceToEarningsRatio ?? (earningsYield > 0 ? 1 / earningsYield : 0)) || 0;

        const fcfYield = m.fcfYield ?? m.freeCashFlowYield ?? 0;

        // ============================================================
        // HARD DISQUALIFIERS — Buffett "durability / too-hard" filter ONLY.
        // Removes the UNINVESTABLE (unpredictable cash, un-serviceable leverage,
        // capital destruction). Quality and PRICE are scored/routed below, not gated.
        // ============================================================

        // 1. Earnings power: 5yr of positive net income.
        const netIncomes = incomeData.slice(0, 5).map(d => d.netIncome);
        if (netIncomes.some(ni => typeof ni !== 'number' || ni <= 0)) { gateRejections.netIncomeNeg++; return null; }

        // 2. Cash generation: 5yr of positive free cash flow.
        const freeCashFlows = cashData.slice(0, 5).map(d => d.freeCashFlow);
        if (freeCashFlows.some(fcf => typeof fcf !== 'number' || fcf <= 0)) { gateRejections.fcfNeg++; return null; }

        // 3. Predictability: free cash flow not wildly erratic (>1 swing of >40%).
        let fcfSwings = 0;
        for (let j = 0; j < 4; j++) {
          const prev = freeCashFlows[j + 1];
          const curr = freeCashFlows[j];
          if (prev > 0) {
            const swing = Math.abs(curr - prev) / prev;
            if (swing > 0.40) fcfSwings++;
          }
        }
        if (fcfSwings > 1) { gateRejections.fcfSwings++; return null; }

        // 4. Leverage SERVICEABILITY — the Buffett leverage test (replaces the blunt
        //    D/E < 1 cliff). Interest coverage > 5x.
        const operatingIncome = inc0.operatingIncome || 0;
        const interestExpense = inc0.interestExpense || 0;
        let interestCoverage = 999;
        if (interestExpense > 0) {
          interestCoverage = operatingIncome / interestExpense;
        }
        if (interestCoverage <= 5) { gateRejections.interestCoverage++; return null; }

        // 5. Capital-productivity FLOOR (not the 12% quality bar — only excludes
        //    capital-destructive businesses; the rest is scored).
        if (roic <= ROIC_FLOOR) { gateRejections.roicFloor++; return null; }

        // --- Non-gating metrics (feed scoring / display only) ---
        const rev0 = inc0.revenue || 0;
        let cagr5y = 0;
        if (incomeData.length >= 6) {
          const rev5 = incomeData[5].revenue;
          if (rev5 > 0) cagr5y = Math.pow(rev0 / rev5, 1 / 5) - 1;
        } else if (incomeData.length === 5) {
          const rev4 = incomeData[4].revenue;
          if (rev4 > 0) cagr5y = Math.pow(rev0 / rev4, 1 / 4) - 1;
        }

        const grossMargin = (inc0.grossProfit || 0) / (inc0.revenue || 1);

        const netDebt = totalDebt0 - cash0;
        const ebitda = inc0.ebitda || (inc0.operatingIncome + (cashData[0].depreciationAndAmortization || 0)) || 1;
        const netDebtToEbitda = netDebt / ebitda;

        // 6. Owner earnings (Buffett's metric) — must be rising over 5yr.
        let oeSeries: number[] = [];
        let ownerEarningsApprox = false;
        const ownerEarningsData = Array.isArray(ownerEarningsDataRaw) ? ownerEarningsDataRaw : [];
        if (ownerEarningsData.length >= 5 && ownerEarningsData[0].ownersEarnings !== undefined) {
          for (let j = 0; j < 5; j++) oeSeries.push(ownerEarningsData[j].ownersEarnings);
        } else {
          ownerEarningsApprox = true;
          for (let j = 0; j < 5; j++) {
            const ocf = cashData[j].operatingCashFlow || 0;
            const da = cashData[j].depreciationAndAmortization || 0;
            oeSeries.push(ocf - da);
          }
        }
        if (oeSeries[0] <= oeSeries[4]) { gateRejections.oeNotRising++; return null; }

        let ownerEarningsTrend = 'Positive';
        const oeChange = (oeSeries[0] - oeSeries[4]) / Math.abs(oeSeries[4] || 1);
        if (oeChange > 0.05) ownerEarningsTrend = 'Rising';
        else if (oeChange >= -0.05 && oeChange <= 0.05) ownerEarningsTrend = 'Flat';
        else ownerEarningsTrend = 'Declining';

        // PRICE ROUTING (deterministic; NEVER a quality gate). FCF yield is the price
        // signal: at/above threshold -> results; below -> watch list (nearMisses).
        const pricedIn = fcfYield >= ROUTING_FCF_YIELD;

        return {
          ticker: c.symbol,
          name: c.name,
          sector: c.sector,
          industry: c.industry,
          currentPrice: c.price,
          pricedIn,
          metrics: {
            roic: roic * 100,
            roe: roe * 100,
            debtToEquity,
            interestCoverage,
            peRatio,
            fcfYield: fcfYield * 100,
            revenueGrowth5Yr: cagr5y * 100,
            grossMargin: grossMargin * 100,
            netDebtToEbitda,
            ownerEarningsTrend
          },
          fcfConsistencyFlag: false,
          ownerEarningsApprox,
          qualityComponents: computeQualityComponents({
            roic: roic * 100,
            fcfYield: fcfYield * 100,
            revenueGrowth5Yr: cagr5y * 100,
            grossMargin: grossMargin * 100,
            netDebtToEbitda,
            interestCoverage,
            ownerEarningsTrend,
          }),
        };
      } catch (e) {
        return null;
      }
    });

    const results = await Promise.all(batchPromises);
    for (const r of results) {
      if (r) {
        survivors.push(r);
      }
    }
    // Throttle outbound FMP calls — Premium plan limit is 300 req/min.
    // BATCH_SIZE=5 × 5 endpoints = 25 concurrent calls per batch; pause between
    // batches to stay well under the limit.
    if (i + BATCH_SIZE < candidates.length) {
      await new Promise(resolve => setTimeout(resolve, 400));
    }
  }

  // --- Stage 2 diagnostic — persisted to Firestore via screenerRunLog ---
  console.log(`[Screener][DIAG] Stage 2 durability rejections: ${JSON.stringify(gateRejections)}`);

  if (survivors.length === 0) {
    console.log(`[Screener] Stage 2: 0 durable businesses survived the hard filters.`);
    return {
      results: [],
      nearMisses: [],
      diagnostics: {
        weekGroup,
        activeSectors,
        stage1: { rawCandidates: stage1Raw, finalCandidates: candidates.length },
        stage2: { rejections: gateRejections, survivors: 0, qualifiedSurvivors: 0, watchSurvivors: 0 },
        stage3: { resultsCount: 0, nearMissesCount: 0 },
      },
    };
  }

  // Deterministic PRICE routing: durable businesses split into sensibly-priced
  // (results) vs wonderful-but-rich (watch list / nearMisses). Quality is NOT gated.
  const qualifiedSurvivors = survivors.filter(s => s.pricedIn);
  const watchSurvivors = survivors.filter(s => !s.pricedIn);
  console.log(`[Screener] Stage 2: ${survivors.length} durable businesses (${qualifiedSurvivors.length} sensibly priced, ${watchSurvivors.length} on watch for price).`);

  const stripForPrompt = (arr: QualifiedCandidate[]) => arr.map(s => ({
    ticker: s.ticker,
    name: s.name,
    sector: s.sector,
    industry: s.industry,
    metrics: s.metrics,
    ownerEarningsApprox: s.ownerEarningsApprox
  }));

  const prompt = `You are a value-investing analyst in the tradition of Warren Buffett and Charlie Munger. You are given two lists of businesses that have ALREADY PASSED a deterministic durability filter (5yr positive and non-erratic free cash flow, 5yr positive net income, interest coverage > 5x, rising owner earnings, and a minimum return-on-invested-capital floor). All numbers are computed deterministically from SEC filings via FMP and are FINAL.

    DO NOT VERIFY, RECOMPUTE, OR RETRIEVE ANY NUMBERS. Use ONLY the provided JSON. Your job is purely qualitative judgment. Do NOT drop a business merely for being expensive — pricing is already handled; the WATCHLIST list IS the expensive-but-wonderful bucket.

    QUALIFIED (durable AND sensibly priced today -> become "results"):
    ${JSON.stringify(stripForPrompt(qualifiedSurvivors), null, 2)}

    WATCHLIST (durable but currently rich on a free-cash-flow-yield basis -> become "nearMisses": wonderful businesses to wait on for a better entry price):
    ${JSON.stringify(stripForPrompt(watchSurvivors), null, 2)}

    TASKS:
    1. STRUCTURAL IMPAIRMENT CHECK: if a business (in EITHER list) shows two or more of the following, EXCLUDE it from your output entirely:
       a) Cash-flow engine structurally impaired by a permanent cost-structure change.
       b) Moat protects users but not revenue (moat-revenue misalignment).
       c) Management instability during a strategic transition.
       d) Monetization model misaligned with cost structure.
    2. For each remaining QUALIFIED business, produce a "results" entry: { "ticker", "rationale" (one sentence on moat + capital allocation), "qualityScore" (0-100), "qualityLabel" }.
       Scoring is judgment using the provided numbers as evidence (do NOT re-derive them): moat durability, ROIC level/stability, FCF consistency, capital-allocation discipline.
       Labels: Elite (85-100), High (70-84), Solid (55-69), Watch (0-54).
    3. For each remaining WATCHLIST business, produce a "nearMisses" entry: { "ticker", "whyNoteworthy" (one sentence on why it is a wonderful business), "watchTrigger" (the price / FCF-yield condition that would make it actionable) }.

    Output ONLY the qualitative fields above. Do NOT output any numeric metrics — they are already known and will be merged back in.`;

  const runScreener = async (modelName: string) => {
    return await withRetry(async () => {
      const config: any = {
        thinking_level: MODELS.FUNNEL.thinkingLevel,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            results: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  ticker: { type: Type.STRING },
                  rationale: { type: Type.STRING },
                  qualityScore: { type: Type.NUMBER },
                  qualityLabel: { type: Type.STRING }
                },
                required: ["ticker", "rationale", "qualityScore", "qualityLabel"]
              }
            },
            nearMisses: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  ticker:        { type: Type.STRING },
                  whyNoteworthy: { type: Type.STRING },
                  watchTrigger:  { type: Type.STRING }
                },
                required: ["ticker", "whyNoteworthy", "watchTrigger"]
              }
            }
          },
          required: ["results", "nearMisses"]
        },
        tools: []
      };

      const currentPrompt = prompt + `\n\nCRITICAL: Your output MUST be a valid JSON object matching the requested schema exactly. ESCAPE ALL INNER DOUBLE QUOTES (\\") to avoid JSON parsing errors. DO NOT use markdown code block wrappers like \`\`\`json.`;

      const response = await callServerAI({
        model: modelName,
        contents: currentPrompt,
        config
      });
      return response;
    }, 5);
  };

  try {
    let response;
    try {
      response = await runScreener(MODELS.FUNNEL.primary);
    } catch (primaryErr: any) {
      const errorStr = typeof primaryErr === 'string' ? primaryErr : (primaryErr instanceof Error ? primaryErr.message : JSON.stringify(primaryErr));
      const combinedMsg = errorStr.toLowerCase();
      const isOverloaded = combinedMsg.includes("high demand") || combinedMsg.includes("503") || combinedMsg.includes("unavailable") || combinedMsg.includes("overloaded");
      if (isOverloaded) {
        console.warn(`[Screener] Primary model unavailable, falling back to ${MODELS.FUNNEL.fallback}...`);
        response = await runScreener(MODELS.FUNNEL.fallback);
      } else {
        throw primaryErr;
      }
    }

    let responseText = extractText(response);
    if (!responseText || responseText.trim() === "") {
      throw new Error("Gemini returned an empty response.");
    }

    const cleaned = cleanJson(responseText);
    let parsed: any;
    try {
      parsed = JSON.parse(cleaned);
    } catch (parseErr: any) {
      console.error("[Screener] Failed to parse model output:", cleaned.substring(0, 500));
      throw new Error(`Model returned invalid JSON: ${parseErr.message}`);
    }

    // Merge: results come from the priced-in pool; nearMisses from the watch pool.
    // failedCriteria is built deterministically in TS (the valuation reason), never by the model.
    const qualifiedMap = new Map(qualifiedSurvivors.map(s => [s.ticker, s]));
    const watchMap = new Map(watchSurvivors.map(s => [s.ticker, s]));

    const parsedResults = Array.isArray(parsed) ? parsed : (parsed.results || []);
    const mergedResults = [];
    for (const res of parsedResults) {
      const candidate = qualifiedMap.get(res.ticker);
      if (candidate) {
        mergedResults.push({
          ...candidate,
          rationale: res.rationale || '',
          qualityScore: res.qualityScore || 0,
          qualityLabel: res.qualityLabel || 'Watch'
        });
      }
    }

    const parsedNearMisses = parsed.nearMisses && Array.isArray(parsed.nearMisses) ? parsed.nearMisses : [];
    const mergedNearMisses = [];
    for (const nm of parsedNearMisses) {
      const candidate = watchMap.get(nm.ticker);
      if (candidate) {
        const fcfYieldPct = candidate.metrics.fcfYield;
        mergedNearMisses.push({
          ...candidate,
          failedCriteria: [`Valuation: FCF yield ${fcfYieldPct.toFixed(1)}% is below the ${(ROUTING_FCF_YIELD * 100).toFixed(0)}% entry threshold`],
          whyNoteworthy: nm.whyNoteworthy || '',
          watchTrigger: nm.watchTrigger || ''
        });
      }
    }

    fetch("/api/telemetry", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ event: "funnel_run", properties: { ticker: "BASKET", resultCount: mergedResults.length, watchCount: mergedNearMisses.length, uid: auth.currentUser?.uid || 'anonymous' } }) }).catch(e => console.error(e));

    return {
      results: mergedResults,
      nearMisses: mergedNearMisses,
      diagnostics: {
        weekGroup,
        activeSectors,
        stage1: { rawCandidates: stage1Raw, finalCandidates: candidates.length },
        stage2: {
          rejections: gateRejections,
          survivors: survivors.length,
          qualifiedSurvivors: qualifiedSurvivors.length,
          watchSurvivors: watchSurvivors.length,
        },
        stage3: { resultsCount: mergedResults.length, nearMissesCount: mergedNearMisses.length },
      },
    };
  } catch (e: any) {
    console.error("Screener Error:", e);
    throw e;
  }
}
export async function getHistoricalData(ticker: string, range: string): Promise<PricePoint[]> {
  // Helper to fetch history for a single ticker
  const fetchHistory = async (symbol: string, timeRange: string): Promise<PricePoint[]> => {
    // Try FMP first
    try {
      let timeseries = 30;
      if (timeRange === '1D') timeseries = 1;
      else if (timeRange === '5D') timeseries = 5;
      else if (timeRange === '1M') timeseries = 30;
      else if (timeRange === '6M') timeseries = 180;
      else if (timeRange === 'YTD') timeseries = 260;
      else if (timeRange === '1Y') timeseries = 260;
      else if (timeRange === '5Y') timeseries = 1250;
      else if (timeRange === 'Max') timeseries = 5000;

      if (timeRange === '1D' || timeRange === '5D' || timeRange === '1M') {
        try {
          const chartData = await fetchFMP(`stable/historical-chart/4hour`, { symbol });
          if (Array.isArray(chartData) && chartData.length > 0) {
            return chartData.map((item: any) => ({
              date: item.date,
              price: item.close
            })).reverse();
          }
        } catch (e) {
          console.warn(`FMP 4-hour chart failed for ${symbol}:`, e);
        }
      }

      if (timeRange === 'Max') {
        // Use standard stable endpoint for full history
        const data = await fetchFMP(`stable/historical-price-eod/full`, { symbol });
        if (data && data.historical) {
          return data.historical.reverse().map((item: any) => ({
            date: item.date,
            price: item.close
          }));
        }
      }

      const data = await fetchFMP(`stable/historical-price-eod/full`, { symbol, timeseries: timeseries.toString() });
      if (data && data.historical) {
        return data.historical.reverse().map((item: any) => ({
          date: item.date,
          price: item.close
        }));
      }
    } catch (err) {
      console.error(`FMP History fetch failed for ${symbol}:`, err);
    }

    // Try Polygon as fallback
    try {
      let multiplier = 1;
      let timespan = 'day';
      let from = '';
      const to = new Date().toISOString().split('T')[0];
      const now = new Date();

      if (timeRange === '1D') {
        multiplier = 5;
        timespan = 'minute';
        from = new Date(now.getTime() - (24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      } else if (timeRange === '5D') {
        multiplier = 30;
        timespan = 'minute';
        from = new Date(now.getTime() - (5 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      } else if (timeRange === '1M') {
        from = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      } else if (timeRange === '6M') {
        from = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      } else if (timeRange === 'YTD') {
        from = `${now.getFullYear()}-01-01`;
      } else if (timeRange === '1Y') {
        from = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      } else if (timeRange === '5Y') {
        multiplier = 1;
        timespan = 'week';
        from = new Date(now.getTime() - (5 * 365 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
      } else if (timeRange === 'Max') {
        multiplier = 1;
        timespan = 'week'; // Use week for Max to stay under 5000 limit while maintaining decent granularity
        from = '2000-01-01';
      }

      const polyData = await fetchPolygon(`v2/aggs/ticker/${symbol}/range/${multiplier}/${timespan}/${from}/${to}`, {
        adjusted: 'true',
        sort: 'asc',
        limit: '5000'
      });

      if (polyData && polyData.results) {
        return polyData.results.map((item: any) => ({
          date: new Date(item.t).toISOString().split('T')[0],
          price: item.c
        }));
      }
    } catch (err) {
      console.error(`Polygon History fetch failed for ${symbol}:`, err);
    }

    return [];
  };

  // Fetch stock and SPY sequentially to respect rate limits
  let stockPoints = await fetchHistory(ticker, range);
  
  // Fallback for Max if it returns nothing
  if (stockPoints.length === 0 && range === 'Max') {
    console.warn(`Max range failed for ${ticker}, falling back to 10Y...`);
    stockPoints = await fetchHistory(ticker, '10Y');
  }
  
  if (stockPoints.length === 0) return [];
  
  let spyPoints = await fetchHistory('SPY', range);
  if (spyPoints.length === 0 && range === 'Max') {
    spyPoints = await fetchHistory('SPY', '10Y');
  }

  // Merge datasets by date with fallback to last known SPY price
  const spyMap = new Map(spyPoints.map(p => [p.date.split(' ')[0], p.price]));
  let lastKnownSpyPrice: number | undefined = undefined;
  
  const mergedPoints = stockPoints.map(p => {
    const dateKey = p.date.split(' ')[0];
    const currentSpyPrice = spyMap.get(dateKey);
    if (currentSpyPrice !== undefined) {
      lastKnownSpyPrice = currentSpyPrice;
    }
    
    return {
      ...p,
      spyPrice: currentSpyPrice || lastKnownSpyPrice
    };
  });

  // Downsample for performance on long ranges while ensuring we have enough points to show a line
  const targetPoints = 150;
  if (mergedPoints.length > targetPoints) {
    const step = Math.ceil(mergedPoints.length / targetPoints);
    return mergedPoints.filter((_: any, i: number) => i % step === 0);
  }

  return mergedPoints;
}


